import { useState } from "react";
import { KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { EmptyState, ErrorState, LoadingState, Spinner } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ROLE_LABELS, formatDate, valueLabel } from "@/lib/labels";

export const StatusDialog = ({ member, onClose, onDone }) => {
  const next = member?.status === "active" ? "inactive" : "active";
  return (
    <ConfirmReasonDialog open={!!member} onOpenChange={(o) => !o && onClose()} danger={next === "inactive"} testId="member-status-dialog"
      title={next === "inactive" ? "Nonaktifkan anggota?" : "Aktifkan kembali anggota?"}
      description={member ? `${member.full_name} akan menjadi ${next === "inactive" ? "Nonaktif (hanya dapat melihat data)" : "Aktif"}.` : ""}
      confirmLabel={next === "inactive" ? "Nonaktifkan" : "Aktifkan"}
      onConfirm={async (reason) => { await api.patch(`/school/members/${member.id}/status`, { status: next, reason }); onDone("Status anggota diperbarui."); }} />
  );
};

export const RoleDialog = ({ member, roles, onClose, onDone }) => {
  const [role, setRole] = useState("");
  return (
    <ConfirmReasonDialog open={!!member} onOpenChange={(o) => { if (!o) { setRole(""); onClose(); } }} testId="member-role-dialog"
      title="Ubah peran anggota" description={member ? `Peran saat ini: ${ROLE_LABELS[member.role]}.` : ""} confirmLabel="Simpan peran"
      onConfirm={async (reason) => {
        if (!role) throw { response: { data: { detail: "Pilih peran baru terlebih dahulu." } } };
        await api.patch(`/school/members/${member.id}/role`, { role, reason });
        setRole("");
        onDone("Peran anggota diperbarui.");
      }}>
      <div className="space-y-2">
        <Label>Peran baru</Label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger aria-label="Peran baru" data-testid="member-new-role-select"><SelectValue placeholder="Pilih peran" /></SelectTrigger>
          <SelectContent>{roles.filter((r) => r !== member?.role).map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </ConfirmReasonDialog>
  );
};

const CHANGE_LABELS = { created: "Dibuat", status: "Perubahan status", role: "Perubahan peran", registration_numbers: "Perubahan nomor registrasi", person: "Perubahan data anggota" };

const fmtChange = (obj) => (obj ? Object.values(obj).map(valueLabel).join(", ") : "—");

const STAFF_ROLES = ["kepala_sekolah", "operator", "guru", "wali_kelas"];

// Foto disimpan sebagai JPEG base64 di data anggota, jadi diperkecil di browser sebelum dikirim.
const kecilkanFoto = async (berkas) => {
  if (!berkas.type?.startsWith("image/")) throw new Error("Pilih berkas gambar (JPEG atau PNG).");
  const gambar = await createImageBitmap(berkas, { imageOrientation: "from-image" }).catch(() => null);
  if (!gambar) throw new Error("Gambar tidak bisa dibaca. Coba foto lain.");
  const skala = Math.min(1, 480 / Math.max(gambar.width, gambar.height));
  const kanvas = document.createElement("canvas");
  kanvas.width = Math.round(gambar.width * skala);
  kanvas.height = Math.round(gambar.height * skala);
  const ctx = kanvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, kanvas.width, kanvas.height);
  ctx.drawImage(gambar, 0, 0, kanvas.width, kanvas.height);
  gambar.close?.();
  const hasil = kanvas.toDataURL("image/jpeg", 0.82);
  if (hasil.length > 400_000) throw new Error("Foto masih terlalu besar. Coba foto dengan cahaya lebih sederhana.");
  return hasil;
};

export const HistoryDialog = ({ member, onClose, canEdit = false, roles = [], onEditStatus, onEditRole, onResetPassword, onSaved }) => {
  const q = useQuery({ queryKey: ["member-history", member?.id], queryFn: () => fetcher(`/school/members/${member.id}/history`), enabled: !!member });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [fotoBerubah, setFotoBerubah] = useState(false);

  const isStudent = member?.role === "siswa";
  const isEmployee = STAFF_ROLES.includes(member?.role);
  const regField = isStudent ? "student_registration_number" : isEmployee ? "employee_registration_number" : null;
  const regLabel = isStudent ? "NIS/NIPD/NISM/NISD" : "NUPTK/NITK/NRG";

  const close = () => { setEditing(false); setErr(""); setReason(""); setFotoBerubah(false); onClose(); };

  const startEdit = () => {
    setForm({
      full_name: member.full_name || "",
      email: member.contact_email || "",
      phone: member.phone || "",
      gender: member.gender || "",
      birth_date: member.birth_date || "",
      registration: (regField && member[regField]) || "",
      photo: "",
    });
    setReason("");
    setErr("");
    setFotoBerubah(false);
    setEditing(true);
    // Foto tidak ikut daftar anggota (ukurannya besar), jadi diambil saat form dibuka.
    fetcher(`/school/members/${member.id}/photo`)
      .then(({ photo }) => setForm((f) => (f.photo ? f : { ...f, photo: photo || "" })))
      .catch(() => {});
  };

  const pilihFoto = async (e) => {
    const berkas = e.target.files?.[0];
    e.target.value = "";
    if (!berkas) return;
    try {
      const hasil = await kecilkanFoto(berkas);
      setForm((f) => ({ ...f, photo: hasil }));
      setFotoBerubah(true);
      setErr("");
    } catch (galat) {
      setErr(galat.message || "Foto gagal diproses. Coba berkas lain.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (reason.trim().length < 5) return setErr("Alasan wajib diisi, minimal 5 karakter.");
    setSaving(true);
    setErr("");
    try {
      const personPayload = {};
      const orig = {
        full_name: member.full_name || "",
        email: member.contact_email || "",
        phone: member.phone || "",
        gender: member.gender || "",
        birth_date: member.birth_date || "",
      };
      if (form.full_name.trim() !== orig.full_name) personPayload.full_name = form.full_name.trim();
      if (form.email.trim() !== orig.email) personPayload.email = form.email.trim() || null;
      if (form.phone.trim() !== orig.phone) personPayload.phone = form.phone.trim() || null;
      if ((form.gender || "") !== orig.gender) personPayload.gender = form.gender || null;
      if (form.birth_date.trim() !== orig.birth_date) personPayload.birth_date = form.birth_date.trim() || null;
      if (fotoBerubah) personPayload.photo = form.photo || null;

      const regChanged = regField && form.registration.trim() !== ((member[regField] || ""));
      if (!Object.keys(personPayload).length && !regChanged) {
        setErr("Tidak ada perubahan yang disimpan.");
        setSaving(false);
        return;
      }

      if (Object.keys(personPayload).length) {
        await api.patch(`/school/members/${member.id}/person`, { ...personPayload, reason: reason.trim() });
      }
      if (regChanged) {
        await api.patch(`/school/members/${member.id}/registration-numbers`, { [regField]: form.registration.trim() || null, reason: reason.trim() });
      }
      setEditing(false);
      setFotoBerubah(false);
      onSaved?.("Data anggota diperbarui.");
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="member-history-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Detail anggota · {member?.full_name}</DialogTitle></DialogHeader>

        {member && !editing && (
          <dl className="grid grid-cols-2 gap-3 rounded-lg bg-muted p-4 text-sm" data-testid="member-detail">
            <div><dt className="text-xs text-muted-foreground">Peran</dt><dd className="font-medium">{ROLE_LABELS[member.role]}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Status</dt><dd className="font-medium">{valueLabel(member.status)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Jenis kelamin</dt><dd>{member.gender || "—"}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Tanggal lahir</dt><dd>{member.birth_date || "—"}</dd></div>
            <div><dt className="text-xs text-muted-foreground">{member.nisn ? "NISN" : "NIP"}</dt><dd className="font-mono">{member.nisn || member.nip || "—"}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Telepon</dt><dd>{member.phone || "—"}</dd></div>
            {member.role === "siswa" && (
              <div><dt className="text-xs text-muted-foreground">NIS/NIPD/NISM/NISD</dt><dd className="font-mono">{member.student_registration_number || "—"}</dd></div>
            )}
            {STAFF_ROLES.includes(member.role) && (
              <div><dt className="text-xs text-muted-foreground">NUPTK/NITK/NRG</dt><dd className="font-mono">{member.employee_registration_number || "—"}</dd></div>
            )}
            <div className="col-span-2"><dt className="text-xs text-muted-foreground">Email kontak</dt><dd className="break-all">{member.contact_email || "—"}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-muted-foreground">Email akun (login)</dt><dd className="break-all">{member.email || "Belum punya akun"}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Berlaku sejak</dt><dd>{formatDate(member.valid_from)}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Alasan terakhir</dt><dd>{member.reason || "—"}</dd></div>
          </dl>
        )}

        {member && editing && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="member-edit-form">
            <div className="space-y-1.5">
              <Label htmlFor="edit-full-name">Nama lengkap</Label>
              <Input id="edit-full-name" value={form.full_name || ""} onChange={set("full_name")} maxLength={150} data-testid="edit-full-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-photo">Foto kartu</Label>
              <div className="flex items-start gap-3">
                {form.photo
                  ? <img src={form.photo} alt={`Pratinjau foto ${member.full_name}`} className="h-24 w-[72px] rounded-md object-cover ring-1 ring-slate-200" data-testid="edit-photo-preview" />
                  : <div className="flex h-24 w-[72px] items-center justify-center rounded-md bg-muted px-1 text-center text-[11px] text-muted-foreground">Belum ada foto</div>}
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Input id="edit-photo" type="file" accept="image/*" onChange={pilihFoto} className="text-xs file:mr-2 file:rounded file:border-0 file:bg-slate-900/5 file:px-2 file:py-1 file:text-xs" data-testid="edit-photo-input" />
                  {!!form.photo && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" data-testid="edit-photo-clear"
                      onClick={() => { setForm((f) => ({ ...f, photo: "" })); setFotoBerubah(true); }}>Hapus foto</Button>
                  )}
                  <p className="text-xs text-muted-foreground">Pas foto formal. Berkas besar otomatis diperkecil sebelum disimpan, lalu tampil di kartu ID dan layar pemindai.</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-gender">Jenis kelamin</Label>
                <Select value={form.gender || "kosong"} onValueChange={(v) => setForm((f) => ({ ...f, gender: v === "kosong" ? "" : v }))}>
                  <SelectTrigger id="edit-gender" aria-label="Jenis kelamin" data-testid="edit-gender-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kosong">Tidak diisi</SelectItem>
                    <SelectItem value="Laki-laki">Laki-laki</SelectItem>
                    <SelectItem value="Perempuan">Perempuan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-birth-date">Tanggal lahir</Label>
                <Input id="edit-birth-date" value={form.birth_date || ""} onChange={set("birth_date")} placeholder="YYYY-MM-DD" maxLength={30} data-testid="edit-birth-date-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">Telepon</Label>
              <Input id="edit-phone" value={form.phone || ""} onChange={set("phone")} maxLength={30} data-testid="edit-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">Email kontak</Label>
              <Input id="edit-email" type="email" value={form.email || ""} onChange={set("email")} maxLength={200} data-testid="edit-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{member.nisn ? "NISN" : "NIP"}</Label>
                <Input value={member.nisn || member.nip || "—"} readOnly disabled className="font-mono" />
                <p className="text-xs text-muted-foreground">Identitas kunci tidak dapat diubah di sini.</p>
              </div>
              {regField && (
                <div className="space-y-1.5">
                  <Label htmlFor="edit-registration">{regLabel}</Label>
                  <Input id="edit-registration" value={form.registration || ""} onChange={set("registration")} maxLength={50} className="font-mono" data-testid="edit-registration-input" />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-reason">Alasan perubahan (wajib, tercatat di audit)</Label>
              <Textarea id="edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tuliskan alasan perubahan data ini" data-testid="edit-reason-input" />
            </div>
            {err && <p className="text-sm text-[#E11D2E]" role="alert" data-testid="edit-error">{err}</p>}
          </div>
        )}

        {member && canEdit && !editing && (
          <div className="flex flex-wrap justify-end gap-2">
            {member.has_account && (
              <Button variant="outline" onClick={() => onResetPassword?.(member)} data-testid="member-reset-password-button">
                <KeyRound className="h-4 w-4 mr-1.5" /> Kirim tautan reset
              </Button>
            )}
            <Button variant="outline" onClick={() => onEditStatus?.(member)} data-testid="member-edit-status-button">Ubah status</Button>
            <Button variant="outline" onClick={() => onEditRole?.(member)} data-testid="member-edit-role-button">Ubah peran</Button>
            <Button onClick={startEdit} data-testid="member-edit-button">Edit</Button>
          </div>
        )}

        {member && editing && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setErr(""); }} disabled={saving} data-testid="member-edit-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="member-edit-save-button">{saving && <Spinner />} Simpan perubahan</Button>
          </div>
        )}

        <p className="text-sm font-semibold">Riwayat</p>
        {q.isLoading ? <LoadingState rows={2} /> : q.error ? <ErrorState error={q.error} /> : !q.data?.length ? <EmptyState title="Belum ada riwayat" /> : (
          <ol className="space-y-3 max-h-[60vh] overflow-y-auto">
            {q.data.map((h) => (
              <li key={h.id} className="rounded-lg border p-3 text-sm">
                <p className="font-semibold">{CHANGE_LABELS[h.change] || h.change} · <span className="text-xs font-normal text-muted-foreground">{formatDate(h.at)}</span></p>
                <p>{fmtChange(h.before)} → {fmtChange(h.after)}</p>
                <p className="text-xs text-muted-foreground">Oleh {h.actor_email || "Sistem"} · Alasan: {h.reason || "—"}</p>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};
